import type { IShowVideoViewerProps } from "@components/modals/videoViewer";
import { VideoViewer } from "@components/modals/videoViewer";
import type { IShowAudioViewerProps } from "@components/modals/audioViewer";
import { AudioViewer } from "@components/modals/audioViewer";
import type {
  IActionAlertVariantProps,
  IDeleteAlertVariantProps,
  IInformationAlertVariantProps,
  IShowCustomAlertParams,
} from "@components/molecules/alert";
import type { IShowImageViewerProps } from "@components/modals/imageViewer";
import { ImageViewer } from "@components/modals/imageViewer";
import { Icon } from "@components/atoms/icons";
import type { IShowPdfViewerProps } from "@components/modals/pdfViewer";
import { PdfViewer } from "@components/modals/pdfViewer";
import { showAlert } from "@/stores/uiStore";

/**
 * Shows an information alert dialog.
 * @param {IShowCustomAlertParams<IInformationAlertVariantProps>} alertProps - The properties of the information alert.
 */
export const showInformationAlert = (
  alertProps: IShowCustomAlertParams<IInformationAlertVariantProps>,
) => {
  showAlert({ ...alertProps, type: "information" });
};

/**
 * Shows an action alert dialog.
 * @param {IShowCustomAlertParams<IActionAlertVariantProps>} alertProps - The properties of the action alert.
 */
export const showActionAlert = (
  alertProps: IShowCustomAlertParams<IActionAlertVariantProps>,
) => {
  showAlert({ ...alertProps, type: "action" });
};

/**
 * Shows a delete alert dialog.
 * @param {IShowCustomAlertParams<IDeleteAlertVariantProps>} alertProps - The properties of the delete alert.
 */
export const showDeleteAlert = (
  alertProps: IShowCustomAlertParams<IDeleteAlertVariantProps>,
) => {
  showAlert({ ...alertProps, type: "delete" });
};

export const showImageViewer = (alertProps: IShowImageViewerProps) => {
  showAlert({
    type: "information",
    customIcon: alertProps?.icon ? (
      <Icon name={alertProps?.icon} type={"bold-duotone"} />
    ) : (
      <Icon name={"Gallery"} type={"bold-duotone"} />
    ),
    title: alertProps?.title,
    children: <ImageViewer src={alertProps?.src} title={alertProps?.title} />,
    content: alertProps?.date,
  });
};

export const showVideoViewer = (alertProps: IShowVideoViewerProps) => {
  showAlert({
    type: "information",
    customIcon: alertProps?.icon ? (
      <Icon name={alertProps?.icon} type={"bold-duotone"} />
    ) : (
      <Icon name={"VideoFramePlayHorizontal"} type={"bold-duotone"} />
    ),
    title: alertProps?.title,
    children: <VideoViewer src={alertProps?.src} title={alertProps?.title} />,
    content: alertProps?.date,
  });
};

export const showAudioViewer = (alertProps: IShowAudioViewerProps) => {
  showAlert({
    type: "information",
    customIcon: alertProps?.icon ? (
      <Icon name={alertProps?.icon} type={"bold-duotone"} />
    ) : (
      <Icon name={"MusicLibrary"} type={"bold-duotone"} />
    ),
    title: alertProps?.title,
    children: <AudioViewer src={alertProps?.src} title={alertProps?.title} />,
    content: alertProps?.date,
  });
};

export const showPdfViewer = (alertProps: IShowPdfViewerProps) => {
  showAlert({
    type: "information",
    customIcon: alertProps?.icon ? (
      <Icon name={alertProps?.icon} type={"bold-duotone"} />
    ) : (
      <Icon name={"Library"} type={"bold-duotone"} />
    ),
    title: alertProps?.title,
    children: <PdfViewer src={alertProps?.src} title={alertProps?.title} />,
    content: alertProps?.date,
  });
};
